import Box from '@mui/material/Box/Box';
import { MenuItem, Popover, Stack, Tooltip } from '@mui/material';
import {
  IconButton,
  Typography,
  __SWEEP_DESIGN_TYPOGRAPHY_STYLES___,
  colors,
} from '@sweep-io/sweep-design';
import { House, MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  createRef,
  Fragment,
  useLayoutEffect,
  useMemo,
} from 'react';
import React from 'react';
import styled from '@mui/material/styles/styled';
import Button from '@mui/material/Button/Button';
import useResizeObserver from '../../../hooks/useResizeObserver';
import { TruncatedTextTooltip } from '../../common/TruncatedTextTooltip';

/**
 * NOTE: design team will prepare new reusable component for design system
 * this one is temporary and for documentation only!
 */
export interface Breadcrumb {
  value: string;
  label: string;
  onClick: (event: any, breadcrumb: { value: string; label: string }) => void;
}
interface BreadcrumbsProps {
  breadcrumbList: Breadcrumb[];
  // reservedSpace prop no longer needed since layout constraint is handled by parent
  disableOverflowCalculations?: boolean;
}

// Cache interface for measurements
interface MeasurementCache {
  containerWidth: number;
  itemWidths: number[];
  ellipsisButtonWidth: number;
  timestamp: number;
}

export const Breadcrumbs = ({
  breadcrumbList,
  disableOverflowCalculations = false,
}: BreadcrumbsProps) => {
  const [cutOverflowToIndex, setCutOverflowToIndex] = useState(0);
  const [hiddenBreadcrumbs, setHiddenBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [truncatedItems, setTruncatedItems] = useState<Set<number>>(new Set());
  const containerRef = createRef<HTMLElement>();
  const itemRefs = useRef<HTMLElement[]>([]);
  const ellipsisButtonRef = useRef<HTMLElement | null>(null);

  // Cache measurements to avoid redundant DOM reads
  const measurementCacheRef = useRef<MeasurementCache | null>(null);

  // Memoize breadcrumb IDs to detect changes
  const breadcrumbIds = useMemo(
    () => breadcrumbList.map((b) => b.value).join(','),
    [breadcrumbList],
  );

  // Separate calculation function (pure, no DOM access)
  const calculateVisibleItems = useCallback(
    (measurements: MeasurementCache) => {
      // Skip calculations if disabled
      if (disableOverflowCalculations) {
        setCutOverflowToIndex(0);
        return;
      }

      // Always try to show first and last items, hide middle items if needed
      const { containerWidth, itemWidths, ellipsisButtonWidth } = measurements;

      // If we have 2 or fewer items, show all
      if (itemWidths.length <= 2) {
        setCutOverflowToIndex((prev) => (prev !== 0 ? 0 : prev));
        return;
      }

      const firstItemWidth = itemWidths[0] || 0;
      const lastItemWidth = itemWidths[itemWidths.length - 1] || 0;

      // Step 1: Check if everything fits without any truncation
      const totalWidthNeeded = itemWidths.reduce((sum, width) => sum + width, 0);
      if (totalWidthNeeded <= containerWidth) {
        setCutOverflowToIndex((prev) => (prev !== 0 ? 0 : prev));
        return;
      }

      // Step 2: Check if first + last + ellipsis can fit with last item having reasonable space
      const minReasonableLastItemWidth = Math.min(lastItemWidth, 150); // Don't truncate last item too aggressively
      const spaceForFirstEllipsisAndReasonableLast =
        firstItemWidth + ellipsisButtonWidth + minReasonableLastItemWidth;

      if (spaceForFirstEllipsisAndReasonableLast > containerWidth) {
        // Can't even fit first + ellipsis + reasonably-sized last item
        // Fall back to fitting what we can from left to right
        let totalWidth = firstItemWidth + ellipsisButtonWidth;
        let cutIndex = 1;

        for (let i = 1; i < itemWidths.length; i++) {
          if (totalWidth + itemWidths[i] <= containerWidth) {
            totalWidth += itemWidths[i];
            cutIndex = i + 1;
          } else {
            break;
          }
        }

        const finalCutIndex = cutIndex >= itemWidths.length ? 0 : cutIndex;
        setCutOverflowToIndex((prev) => (prev !== finalCutIndex ? finalCutIndex : prev));
        return;
      }

      // Step 3: We can fit first + ellipsis + reasonable last item
      // Hide all middle items to maximize space for the last item
      const cutIndex = itemWidths.length - 1; // Hide everything except first and last
      setCutOverflowToIndex((prev) => (prev !== cutIndex ? cutIndex : prev));
    },
    [disableOverflowCalculations],
  );

  // Simplified visible items calculation with direct DOM measurements
  const updateVisibleItems = useCallback(() => {
    // Skip all calculations if disabled
    if (disableOverflowCalculations) {
      if (cutOverflowToIndex !== 0) {
        setCutOverflowToIndex(0);
      }
      return;
    }

    if (!containerRef.current || itemRefs.current.length < 2) {
      if (cutOverflowToIndex !== 0) {
        setCutOverflowToIndex(0);
      }
      return;
    }

    const now = performance.now();

    // Check if we can use cached measurements (within 100ms)
    if (measurementCacheRef.current && now - measurementCacheRef.current.timestamp < 100) {
      const measurements = measurementCacheRef.current;
      calculateVisibleItems(measurements);
      return;
    }

    // Direct DOM measurements - simple and fast
    const validRefs = itemRefs.current.filter((ref) => !!ref);

    if (validRefs.length === 0) {
      setCutOverflowToIndex(0);
      return;
    }

    // Use container width directly since parent flex layout already constrains us
    const containerWidth = containerRef.current.offsetWidth;
    const availableWidth = containerWidth; // No need to subtract reservedSpace - already constrained by flex

    // Measure all elements at once
    const measurements: MeasurementCache = {
      containerWidth: availableWidth,
      itemWidths: validRefs.map((ref) => ref.offsetWidth),
      ellipsisButtonWidth: ellipsisButtonRef?.current?.offsetWidth ?? 32, // Default ellipsis button width
      timestamp: now,
    };

    // Cache the measurements
    measurementCacheRef.current = measurements;

    // Calculate visible items
    calculateVisibleItems(measurements);
  }, [
    containerRef,
    itemRefs,
    cutOverflowToIndex,
    calculateVisibleItems,
    disableOverflowCalculations,
  ]);

  // Invalidate cache when breadcrumbs change
  useEffect(() => {
    measurementCacheRef.current = null;
  }, [breadcrumbIds]);

  // Function to detect which items are truncated
  const checkForTruncation = useCallback(() => {
    const newTruncatedItems = new Set<number>();

    itemRefs.current.forEach((ref, idx) => {
      if (ref) {
        // Find the button element within this item
        const button = ref.querySelector('button');
        if (button && button.scrollWidth > button.clientWidth) {
          newTruncatedItems.add(idx);
        }
      }
    });

    // Only update state if truncation status changed
    if (
      newTruncatedItems.size !== truncatedItems.size ||
      ![...newTruncatedItems].every((item) => truncatedItems.has(item))
    ) {
      setTruncatedItems(newTruncatedItems);
    }
  }, [truncatedItems]);

  // Debounced resize handler
  const debouncedUpdateRef = useRef<ReturnType<typeof setTimeout>>();
  const handleResize = useCallback(() => {
    // Skip resize handling if calculations are disabled
    if (disableOverflowCalculations) {
      return;
    }

    if (debouncedUpdateRef.current) {
      clearTimeout(debouncedUpdateRef.current);
    }

    // Invalidate cache on resize
    measurementCacheRef.current = null;

    debouncedUpdateRef.current = setTimeout(() => {
      updateVisibleItems();
      // Check for truncation after resize
      setTimeout(checkForTruncation, 10); // Small delay to ensure layout is complete
    }, 16); // One frame delay
  }, [updateVisibleItems, checkForTruncation, disableOverflowCalculations]);

  useResizeObserver({
    ref: containerRef,
    onResize: handleResize,
  });

  // Initial calculation
  useEffect(() => {
    if (disableOverflowCalculations) {
      return;
    }

    if (containerRef.current && itemRefs.current.length > 2) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(updateVisibleItems, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [breadcrumbIds, updateVisibleItems, containerRef, itemRefs, disableOverflowCalculations]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (disableOverflowCalculations) {
      setHiddenBreadcrumbs([]);
      return;
    }

    const isLast = (breadcrumbList?.length ?? 0) - 1 === cutOverflowToIndex;
    const _hidden = breadcrumbList.slice(1, isLast ? cutOverflowToIndex : cutOverflowToIndex + 1);

    //slice don't include last element and startOverflowFromIndex is last element we want to cut
    if (_hidden !== hiddenBreadcrumbs) {
      setHiddenBreadcrumbs(_hidden);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbList, cutOverflowToIndex, disableOverflowCalculations]);

  // Check for truncation after layout changes
  useLayoutEffect(() => {
    if (!disableOverflowCalculations) {
      checkForTruncation();
    }
  }, [breadcrumbList, cutOverflowToIndex, checkForTruncation, disableOverflowCalculations]);

  const renderLabel = (label: string) => {
    return breadcrumbList.length === 2 ? (
      <TruncatedTextTooltip variant="body-medium">{label}</TruncatedTextTooltip>
    ) : (
      label
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        // Removed width: '100%' to respect parent flex constraint
      }}
      ref={containerRef}
    >
      {breadcrumbList.map((breadcrumb, idx) => {
        const isFirstItem = idx === 0;
        const isLastItem = idx === breadcrumbList.length - 1;
        const hideElement = hiddenBreadcrumbs.find((b) => b.value === breadcrumb.value);

        return (
          <Fragment key={'breadcrumb_' + breadcrumb.value}>
            {hiddenBreadcrumbs.length > 0 && idx === cutOverflowToIndex && (
              <BoxWithCenteredContent key={'ellipsis-button-' + breadcrumb.value}>
                <EllipsisPopover hiddenBreadcrumbs={hiddenBreadcrumbs} ref={ellipsisButtonRef} />
                <CustomDivider />
              </BoxWithCenteredContent>
            )}

            <BoxWithCenteredContent
              key={breadcrumb.value}
              sx={{
                ...(hideElement ? hideElementSx : {}),
              }}
              ref={(el) => (itemRefs.current[idx] = el as HTMLElement)}
            >
              {isFirstItem ? (
                <HouseIcon breadcrumb={breadcrumb} />
              ) : (
                <Tooltip
                  title={truncatedItems.has(idx) ? breadcrumb.label : ''}
                  placement="bottom"
                  arrow
                  disableHoverListener={!truncatedItems.has(idx)} // Only show tooltip when actually truncated
                >
                  <StyledButton
                    textColor={isLastItem ? colors.grey[900] : colors.grey[700]}
                    variant="text"
                    onClick={(e) => !isLastItem && breadcrumb.onClick(e, breadcrumb)}
                    sx={{
                      cursor: isLastItem ? 'default' : 'pointer',
                      minWidth: 0,
                      // Last item gets constrained width to allow CSS truncation (unless calculations are disabled)
                      maxWidth: isLastItem && !disableOverflowCalculations ? '176px' : '100%',
                      pl: 1,
                      overflow: disableOverflowCalculations ? 'visible' : 'hidden',
                      textOverflow: disableOverflowCalculations ? 'unset' : 'ellipsis',
                      whiteSpace: disableOverflowCalculations ? 'normal' : 'nowrap',
                    }}
                  >
                    {renderLabel(breadcrumb.label)}
                  </StyledButton>
                </Tooltip>
              )}
              {!isLastItem && <CustomDivider />}
            </BoxWithCenteredContent>
          </Fragment>
        );
      })}
    </Box>
  );
};

const CustomDivider = () => (
  <Stack direction="row" alignItems="center" pl={1}>
    <Typography variant="h4-medium" color={colors.grey[500]}>
      /
    </Typography>
  </Stack>
);

//element needs to be rendered to measure its width
const hideElementSx = {
  visibility: 'hidden',
  position: 'absolute',
  top: '-5000000px',
};

const HouseIcon = ({ breadcrumb }: { breadcrumb: Breadcrumb }) => (
  <BoxWithCenteredContent>
    <IconButton variant="flat" size="tiny" onClick={(e) => breadcrumb.onClick(e, breadcrumb)}>
      <House color={colors.grey[700]} />
    </IconButton>
  </BoxWithCenteredContent>
);

const EllipsisPopover = React.forwardRef<HTMLElement | null, { hiddenBreadcrumbs: Breadcrumb[] }>(
  ({ hiddenBreadcrumbs }, ref) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    return (
      <Box pl={1} ref={ref}>
        <Box onClick={(e) => setAnchorEl(e.currentTarget)}>
          <IconButton variant="flat" size="tiny" pressed={Boolean(anchorEl)}>
            <MoreActionsHorizontal color={colors.grey[800]} />
          </IconButton>
        </Box>

        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 1 }}>
            {hiddenBreadcrumbs.map((breadcrumb) => {
              return (
                <MenuItem key={breadcrumb.value} onClick={(e) => breadcrumb.onClick(e, breadcrumb)}>
                  {breadcrumb.label}
                </MenuItem>
              );
            })}
          </Box>
        </Popover>
      </Box>
    );
  },
);

const BoxWithCenteredContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'textColor' && prop !== 'withEllipsis' && prop !== 'isActive',
})<{ textColor: string; withEllipsis?: boolean; isActive?: boolean }>(
  ({ textColor, withEllipsis, isActive }) => ({
    whiteSpace: 'nowrap',
    color: textColor,
    textOverflow: withEllipsis ? 'ellipsis' : '',
    overflow: withEllipsis ? 'hidden' : '',
    display: 'block',
    minWidth: 0,
    cursor: isActive ? 'default' : 'pointer',

    fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['body-medium']?.fontSize,
    fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['body-medium']?.fontWeight,
    lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['body-medium']?.lineHeight,

    '&:hover': {
      color: colors.grey[900],
    },
  }),
);
